import { GroupStanding, RoundRobinMatch, RoundRobinState } from "@/types/tournament";

const BYE = "__bye__";

/**
 * Circle method for odd N: adds a virtual BYE team to make N even, then generates N-1 rounds
 * where each round has floor(N/2) real matches and the team paired with BYE sits out.
 */
export function generateRoundRobinSchedule(teams: string[]): Array<{
  matches: Array<[string, string]>;
  byeTeam: string | null;
}> {
  if (teams.length < 2) return [];

  const hasOdd = teams.length % 2 !== 0;
  const all = hasOdd ? [BYE, ...teams] : [...teams];
  const n = all.length;
  const fixed = all[0];
  const rotating = all.slice(1);
  const rotLen = rotating.length;

  const rounds: Array<{ matches: Array<[string, string]>; byeTeam: string | null }> = [];

  for (let r = 0; r < n - 1; r++) {
    const rot = [...rotating.slice(rotLen - r), ...rotating.slice(0, rotLen - r)];

    const half = n / 2;
    const topRow = [fixed, ...rot.slice(0, half - 1)];
    const bottomRow = [...rot.slice(half - 1)].reverse();

    const matches: Array<[string, string]> = [];
    let byeTeam: string | null = null;

    for (let i = 0; i < half; i++) {
      const t1 = topRow[i];
      const t2 = bottomRow[i];
      if (t1 === BYE || t2 === BYE) {
        byeTeam = t1 === BYE ? t2 : t1;
      } else {
        matches.push([t1, t2]);
      }
    }

    rounds.push({ matches, byeTeam });
  }

  return rounds;
}

export function buildRoundRobinState(teams: string[]): RoundRobinState {
  const schedule = generateRoundRobinSchedule(teams);
  const matches: RoundRobinMatch[] = [];
  const byeTeamPerRound: (string | null)[] = [];

  schedule.forEach(({ matches: roundMatches, byeTeam }, roundIndex) => {
    byeTeamPerRound.push(byeTeam);
    roundMatches.forEach(([pair1, pair2], matchInRound) => {
      matches.push({
        id: `rr-r${roundIndex}-m${matchInRound}`,
        pair1,
        pair2,
        score1: "",
        score2: "",
        winner: null,
        status: "pending",
        roundIndex,
        roundLabel: `Round ${roundIndex + 1}`,
      });
    });
  });

  return { totalTeams: teams.length, teams, matches, byeTeamPerRound };
}

export function computeRoundRobinStandings(state: RoundRobinState): GroupStanding[] {
  const stats = new Map<string, GroupStanding>();
  const h2hWins = new Map<string, Map<string, number>>();

  for (const team of state.teams) {
    stats.set(team, {
      groupId: "rr",
      groupLabel: "Round Robin",
      team,
      played: 0,
      wins: 0,
      losses: 0,
      pointsFor: 0,
      pointsAgainst: 0,
      differential: 0,
    });
    const inner = new Map<string, number>();
    for (const opp of state.teams) {
      if (opp !== team) inner.set(opp, 0);
    }
    h2hWins.set(team, inner);
  }

  for (const match of state.matches) {
    if (match.status !== "finished" || !match.winner) continue;
    const s1 = Number(match.score1);
    const s2 = Number(match.score2);
    if (!Number.isFinite(s1) || !Number.isFinite(s2)) continue;

    const p1 = stats.get(match.pair1);
    const p2 = stats.get(match.pair2);
    if (!p1 || !p2) continue;

    p1.played++;
    p2.played++;
    p1.pointsFor += s1;
    p1.pointsAgainst += s2;
    p1.differential += s1 - s2;
    p2.pointsFor += s2;
    p2.pointsAgainst += s1;
    p2.differential += s2 - s1;

    if (match.winner === match.pair1) {
      p1.wins++;
      p2.losses++;
      h2hWins.get(match.pair1)!.set(match.pair2, (h2hWins.get(match.pair1)!.get(match.pair2) ?? 0) + 1);
    } else {
      p2.wins++;
      p1.losses++;
      h2hWins.get(match.pair2)!.set(match.pair1, (h2hWins.get(match.pair2)!.get(match.pair1) ?? 0) + 1);
    }
  }

  const standings = Array.from(stats.values());

  standings.sort((a, b) => {
    if (b.wins !== a.wins) return b.wins - a.wins;

    // Head-to-head for 2-way ties
    const aBeatsB = h2hWins.get(a.team)?.get(b.team) ?? 0;
    const bBeatsA = h2hWins.get(b.team)?.get(a.team) ?? 0;
    if (aBeatsB !== bBeatsA) return bBeatsA - aBeatsB;

    if (b.differential !== a.differential) return b.differential - a.differential;
    if (b.pointsFor !== a.pointsFor) return b.pointsFor - a.pointsFor;
    return a.team.localeCompare(b.team);
  });

  // Add tie-break notes for adjacent tied entries
  for (let i = 0; i < standings.length; i++) {
    const curr = standings[i];
    const next = standings[i + 1];
    if (!next || next.wins !== curr.wins) continue;

    const aBeatsB = h2hWins.get(curr.team)?.get(next.team) ?? 0;
    const bBeatsA = h2hWins.get(next.team)?.get(curr.team) ?? 0;

    if (aBeatsB !== bBeatsA) {
      curr.tieBreakNote = "Head-to-head";
    } else if (curr.differential !== next.differential) {
      curr.tieBreakNote = "Point differential";
    }
  }

  return standings;
}

export function normalizeRoundRobinMatch(raw: unknown, index: number): RoundRobinMatch {
  const m = (raw ?? {}) as Partial<RoundRobinMatch>;
  const status =
    m.status === "currently-playing" || m.status === "finished" || m.status === "up-next"
      ? m.status
      : "pending";
  return {
    id: typeof m.id === "string" && m.id.trim() ? m.id.trim() : `rr-match-${index}`,
    pair1: typeof m.pair1 === "string" && m.pair1.trim() ? m.pair1.trim() : "TBD",
    pair2: typeof m.pair2 === "string" && m.pair2.trim() ? m.pair2.trim() : "TBD",
    score1: typeof m.score1 === "string" ? m.score1 : "",
    score2: typeof m.score2 === "string" ? m.score2 : "",
    winner: typeof m.winner === "string" ? m.winner : null,
    status,
    statusSource: m.statusSource === "manual" ? "manual" : m.statusSource === "auto" ? "auto" : undefined,
    roundIndex: typeof m.roundIndex === "number" && Number.isFinite(m.roundIndex) ? m.roundIndex : 0,
    roundLabel: typeof m.roundLabel === "string" && m.roundLabel.trim() ? m.roundLabel.trim() : "Round 1",
  };
}

export function normalizeRoundRobinState(value: unknown): RoundRobinState | null {
  if (!value || typeof value !== "object") return null;
  const raw = value as Partial<RoundRobinState>;
  const teams = Array.isArray(raw.teams)
    ? raw.teams.filter((t): t is string => typeof t === "string" && t.trim().length > 0)
    : [];
  if (teams.length < 2) return null;
  const matches = Array.isArray(raw.matches)
    ? raw.matches.map((m, i) => normalizeRoundRobinMatch(m, i))
    : [];
  const byeTeamPerRound = Array.isArray(raw.byeTeamPerRound)
    ? raw.byeTeamPerRound.map((v) => (typeof v === "string" ? v : null))
    : [];
  return {
    totalTeams: teams.length,
    teams,
    matches,
    byeTeamPerRound,
  };
}
